import * as React from "react";
import { css, Variants } from "@nju/css";
import { Icon, IconName } from "@nju/icons";
import { Box } from "../Box";
import { CircleLoader } from "../Loader";
import { Separator, SeparatorProps } from "../Separator";
import { Text } from "../Text";
import { ArrowButton } from "./ArrowButton";

type DisplayStyleProps = Variants<typeof Box>["display"];

type StyleProps = Variants<typeof styles>;

interface Badge {
  iconName?: IconName;
  body: string | React.ReactNode;
  variant: Variants<typeof styles["badge"]>["variant"];
}

export interface IPanelProps {
  testId?: string;
  iconName?: IconName;
  iconColor?: StyleProps;
  bgColor?: StyleProps;
  width?: "default" | "full";
  height?: "default" | "full";
  title?: string | React.ReactNode;
  forceShowTitleBlock?: boolean;
  isLoading?: boolean;
  isFoldable?: boolean;
  isActive?: boolean;
  withPadding?: boolean;
  withMarginTop?: boolean;
  border?: "default" | "info";
  invisibleSeparator?: boolean;
  separatorSize?: SeparatorProps["size"];
  badge?: Badge | Badge[];
  display?: DisplayStyleProps;
  children:
    | React.ReactNode
    | ((props: { isExpanded: boolean }) => React.ReactNode);
  initiallyExpanded?: boolean;
  textExpanded?: string;
  textCollapsed?: string;
  customPadding?: boolean;
}

export function Panel({
  children,
  display,
  iconName,
  iconColor = "accent",
  bgColor = "white",
  width,
  height = "default",
  title,
  forceShowTitleBlock = false,
  isFoldable = false,
  border = "default",
  isLoading = false,
  invisibleSeparator = false,
  separatorSize = "s",
  badge,
  isActive = true,
  withPadding = true,
  withMarginTop = false,
  testId,
  initiallyExpanded = false,
  textExpanded,
  textCollapsed,
  customPadding = false,
}: IPanelProps) {
  const [isExpanded, setIsExpanded] =
    React.useState<boolean>(initiallyExpanded);

  const onButtonPress = React.useCallback(() => {
    setIsExpanded(!isExpanded);
  }, [isExpanded]);

  const isBadgeVisible =
    badge && (Array.isArray(badge) ? badge.length > 0 : badge.body);

  return (
    <Box
      border={border}
      className={styles.panelWrapper({
        isLoading,
        width,
        height,
        withPadding,
        withMarginTop,
        bgColor,
        customPadding,
      })}
      data-cy={testId}
      display={display}
    >
      {isBadgeVisible &&
        (Array.isArray(badge) ? badge : [badge]).map((item, idx) => {
          return (
            item?.body && (
              <div
                className={styles.badge({ variant: item.variant })}
                key={idx}
              >
                {item.iconName && (
                  <Icon
                    className={styles.badgeIcon({ isActive })}
                    name={item.iconName}
                  />
                )}
                {item.body}
              </div>
            )
          );
        })}
      <div className={styles.loader({ isLoading })}>
        <CircleLoader />
      </div>
      {(title || forceShowTitleBlock) && (
        <>
          <Box align="center" display="flex" justify="spaceBetween">
            <Box align="center" display="flex">
              {iconName && (
                <Icon
                  className={styles.icon({ isActive })}
                  name={iconName}
                  tone={iconColor}
                />
              )}
              {title && (
                <Text
                  size={{ "@onlyMobile": "m", "@fromTablet": "xl" }}
                  weight="bold"
                >
                  {title}
                </Text>
              )}
            </Box>
            {isFoldable && (
              <Box
                align="start"
                className={styles.trigger({ device: "fromTablet" })}
                flexDirection="column"
              >
                <ArrowButton
                  isExpanded={isExpanded}
                  onButtonPress={onButtonPress}
                  textCollapsed={textCollapsed}
                  textExpanded={textExpanded}
                />
              </Box>
            )}
          </Box>
          <Separator
            size={separatorSize}
            tone={invisibleSeparator ? "invisible" : "complementary"}
          />
        </>
      )}

      {typeof children === "function" ? children({ isExpanded }) : children}

      {isFoldable && (
        <Box
          align="start"
          className={styles.trigger({ device: "mobile" })}
          flexDirection="column"
        >
          <Separator size="s" tone="invisible" />
          <ArrowButton
            isExpanded={isExpanded}
            onButtonPress={onButtonPress}
            textCollapsed={textCollapsed}
            textExpanded={textExpanded}
          />
        </Box>
      )}
    </Box>
  );
}

const styles = {
  badge: css({
    variants: {
      variant: {
        ripeLemon: {
          backgroundColor: "$ripeLemon",
          position: "absolute",
          right: -1,
          px: "$25",
          py: "$10",
          borderRadius: "30px 0px 0px 30px",
          fontSize: "$12",
          fontWeight: "700",
        },
        dark: {
          backgroundColor: "$daintree",
          position: "absolute",
          right: 20,
          px: "8px",
          py: "8px",
          borderRadius: "10px",
          fontSize: "$16",
          fontWeight: "800",
          color: "$white",
        },
        cerulean: {
          backgroundColor: "$cerulean",
          position: "absolute",
          right: 70,
          px: "8px",
          py: "8px",
          borderRadius: "10px",
          fontSize: "$16",
          fontWeight: "800",
          color: "$white",
          display: "flex",
          alignItems: "center",
        },
      },
    },
  }),
  badgeIcon: css({
    marginRight: "$10",
  }),
  panelWrapper: css({
    position: "relative",
    borderRadius: "8px",
    width: "100%",
    defaultVariants: {
      width: "default",
    },
    variants: {
      bgColor: {
        ripeLemon: {
          backgroundColor: "$ripeLemon",
        },
        white: {
          backgroundColor: "$white",
        },
      },
      isLoading: {
        true: {
          "&::before": {
            content: "",
            position: "absolute",
            width: "100%",
            height: "100%",
            background: "rgba(255, 255, 255, 0.6)",
            zIndex: "1",
            top: "0",
            left: "0",
          },
        },
      },
      withPadding: {
        true: {
          p: "$20",
          "@onlyMobile": {
            p: "$15",
          },
        },
        false: {
          p: "0",
        },
      },
      customPadding: {
        true: {
          px: "$20",
          py: "$30",
          "@onlyMobile": {
            p: "$15",
          },
        },
      },
      withMarginTop: {
        true: {
          mt: "$20",
        },
        false: {
          mt: "0",
        },
      },
      width: {
        default: {
          maxWidth: "820px",
        },
        full: {
          maxWidth: "100%",
        },
      },
      height: {
        default: {},
        full: {
          height: "100%",
        },
      },
    },
  }),
  loader: css({
    position: "absolute",
    zIndex: 1,
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    variants: {
      isLoading: {
        true: {
          display: "block",
        },
        false: {
          display: "none",
        },
      },
    },
  }),
  icon: css({
    marginRight: "$15",
    w: "$24",
    h: "$24",
    "@fromTablet": {
      w: "$32",
      h: "$32",
    },
    variants: {
      isActive: {
        false: {
          opacity: "50%",
        },
      },
    },
  }),
  trigger: css({
    display: "flex",
    alignItems: "center",
    variants: {
      device: {
        mobile: {
          "@fromTablet": {
            display: "none",
          },
        },
        fromTablet: {
          "@onlyMobile": {
            display: "none",
          },
        },
      },
    },
  }),
};
